import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Save, Trash2, ShieldCheck, Lock, Building2 } from "lucide-react";
import { toast } from "sonner";

const BASE_ROLES = [
  { key: "super_admin", label: "Super Admin" },
  { key: "general_manager", label: "General Manager" },
  { key: "property_admin", label: "Property Admin" },
  { key: "supervisor", label: "Supervisor" },
  { key: "housekeeping", label: "Housekeeping" },
  { key: "maintenance", label: "Maintenance" },
];

export default function Roles() {
  const { refreshPermissions, user } = useAuth();
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [dirtyPerms, setDirtyPerms] = useState(null); // array | null
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ key: "", name: "", base_role: "supervisor", description: "", permissions: [] });
  const [departments, setDepartments] = useState([]);
  const [newDeptName, setNewDeptName] = useState("");
  const [deptBusy, setDeptBusy] = useState(false);
  const [deptToDelete, setDeptToDelete] = useState(null);
  const isSuperAdmin = user?.role === "super_admin";

  const load = useCallback(async () => {
    const [m, r, d] = await Promise.all([
      api.get("/modules"),
      api.get("/roles"),
      api.get("/departments").catch(() => ({ data: [] })),
    ]);
    setModules(m.data);
    setRoles(r.data);
    setDepartments(d.data);
    if (r.data.length && !activeKey) setActiveKey(r.data[0].key);
  }, [activeKey]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    for (const m of modules) {
      (g[m.category] ||= []).push(m);
    }
    return g;
  }, [modules]);

  const active = roles.find((r) => r.key === activeKey);
  const effectivePerms = dirtyPerms ?? (active?.permissions || []);

  const togglePerm = (key) => {
    const next = effectivePerms.includes(key)
      ? effectivePerms.filter((p) => p !== key)
      : [...effectivePerms, key];
    setDirtyPerms(next);
  };

  const save = async () => {
    if (!active || !dirtyPerms) return;
    setBusy(true);
    try {
      await api.put(`/roles/${active.key}`, { permissions: dirtyPerms });
      toast.success("Permissions saved");
      setDirtyPerms(null);
      await load();
      await refreshPermissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => setDirtyPerms(null);

  const submitCreate = async () => {
    if (!createForm.key.trim() || !createForm.name.trim()) {
      toast.error("Key and name are required");
      return;
    }
    try {
      const { data } = await api.post(`/roles`, createForm);
      toast.success(`Role "${data.name}" created`);
      setCreateOpen(false);
      setCreateForm({ key: "", name: "", base_role: "supervisor", description: "", permissions: [] });
      await load();
      setActiveKey(data.key);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteRole = async () => {
    if (!active || active.key === "super_admin") return;
    const systemNote = active.is_system
      ? "\n\nThis is a system role — it will be tombstoned and won't reappear on restart. Use 'Restore defaults' to bring it back."
      : "";
    if (!window.confirm(`Delete role "${active.name}"?${systemNote}`)) return;
    try {
      await api.delete(`/roles/${active.key}`);
      toast.success(active.is_system ? "System role deleted (tombstoned)" : "Role deleted");
      setActiveKey(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const restoreDefaults = async () => {
    if (!window.confirm("Restore all built-in system roles? This re-seeds any roles you previously deleted (general_manager, property_admin, supervisor, housekeeping, maintenance).")) return;
    try {
      const { data } = await api.post("/roles/restore-defaults");
      toast.success(`Restored ${data.restored_count} system role(s).`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleCreatePerm = (key) => {
    setCreateForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key],
    }));
  };

  // -------- Departments management --------
  const createDepartment = async () => {
    const name = newDeptName.trim();
    if (!name) return;
    setDeptBusy(true);
    try {
      await api.post("/departments", { name });
      toast.success(`Department "${name}" created`);
      setNewDeptName("");
      const { data } = await api.get("/departments");
      setDepartments(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create department");
    } finally {
      setDeptBusy(false);
    }
  };

  const deleteDepartment = async (d) => {
    if (d.is_system) {
      toast.error("System departments cannot be deleted");
      return;
    }
    try {
      await api.delete(`/departments/${d.id}`);
      toast.success(`Department "${d.name}" deleted`);
      setDepartments((arr) => arr.filter((x) => x.id !== d.id));
      setDeptToDelete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete department");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Access control</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Roles & Permissions</h1>
          <p className="text-sm text-zinc-500 mt-2">Define which modules each role can access. Changes apply on next login or page refresh.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <div className="flex gap-2">
              <Button variant="outline" onClick={restoreDefaults} data-testid="restore-defaults-button" type="button">Restore defaults</Button>
              <Button className="bg-zinc-950 hover:bg-zinc-800" data-testid="new-role-button"><Plus className="w-4 h-4 mr-2" />New role</Button>
            </div>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>Create custom role</DialogTitle>
              <DialogDescription>Choose a base role and the modules this role can access.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label>Display name</Label>
                  <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g. Front Desk" data-testid="new-role-name" />
                </div>
                <div>
                  <Label>Key (slug)</Label>
                  <Input value={createForm.key} onChange={(e) => setCreateForm({ ...createForm, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })} placeholder="front_desk" data-testid="new-role-key" />
                </div>
              </div>
              <div>
                <Label>Inherits backend permissions from</Label>
                <Select value={createForm.base_role} onValueChange={(v) => setCreateForm({ ...createForm, base_role: v })}>
                  <SelectTrigger data-testid="new-role-base"><SelectValue /></SelectTrigger>
                  <SelectContent>{BASE_ROLES.map((b) => (<SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>))}</SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-500 mt-1">Backend authorization for actions like creating properties, approving leaves, etc. follows the base role.</p>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea rows={2} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} />
              </div>
              <div className="border-t border-zinc-200 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <Label>Module access</Label>
                  <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={modules.length > 0 && createForm.permissions.length === modules.length}
                      ref={(el) => {
                        if (el) el.indeterminate = createForm.permissions.length > 0 && createForm.permissions.length < modules.length;
                      }}
                      onChange={(e) => setCreateForm((f) => ({ ...f, permissions: e.target.checked ? modules.map((m) => m.key) : [] }))}
                      data-testid="new-role-select-all"
                    />
                    Select all ({createForm.permissions.length}/{modules.length})
                  </label>
                </div>
                <div className="mt-2 space-y-3 max-h-64 overflow-auto">
                  {Object.entries(grouped).map(([cat, mods]) => {
                    const catKeys = mods.map((m) => m.key);
                    const catSelected = catKeys.filter((k) => createForm.permissions.includes(k)).length;
                    const allCat = catSelected === catKeys.length;
                    const someCat = catSelected > 0 && catSelected < catKeys.length;
                    return (
                      <div key={cat}>
                        <label className="flex items-center gap-2 cursor-pointer select-none mb-1">
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5"
                            checked={allCat}
                            ref={(el) => { if (el) el.indeterminate = someCat; }}
                            onChange={(e) => {
                              setCreateForm((f) => ({
                                ...f,
                                permissions: e.target.checked
                                  ? Array.from(new Set([...f.permissions, ...catKeys]))
                                  : f.permissions.filter((p) => !catKeys.includes(p)),
                              }));
                            }}
                            data-testid={`new-role-cat-${cat}`}
                          />
                          <span className="label-overline">{cat}</span>
                        </label>
                        <div className="space-y-1 pl-5">
                          {mods.map((m) => (
                            <label key={m.key} className="flex items-center gap-2 p-1.5 hover:bg-zinc-50 rounded cursor-pointer text-sm">
                              <input type="checkbox" checked={createForm.permissions.includes(m.key)} onChange={() => toggleCreatePerm(m.key)} data-testid={`new-role-perm-${m.key}`} />
                              <span>{m.label}</span>
                              <span className="text-[10px] text-zinc-400 font-mono ml-auto">{m.key}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={submitCreate} className="bg-zinc-950" data-testid="new-role-submit">Create role</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Role list */}
        <div className="bg-white border border-zinc-200 rounded-md p-2 h-fit">
          {roles.map((r) => (
            <button
              key={r.key}
              onClick={() => { setActiveKey(r.key); setDirtyPerms(null); }}
              className={`w-full text-left px-3 py-2.5 rounded transition-colors flex items-start gap-2 ${activeKey === r.key ? "bg-zinc-900 text-white" : "hover:bg-zinc-50"}`}
              data-testid={`role-tab-${r.key}`}
            >
              {r.is_system ? <Lock className="w-3.5 h-3.5 mt-1 opacity-60" /> : <ShieldCheck className="w-3.5 h-3.5 mt-1 opacity-80" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{r.name}</div>
                <div className={`text-[10px] font-mono ${activeKey === r.key ? "text-zinc-300" : "text-zinc-500"}`}>{r.key} · {r.permissions.length} perms</div>
              </div>
            </button>
          ))}
        </div>

        {/* Permission editor */}
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          {!active ? (
            <div className="text-center text-zinc-500 py-12">Select a role to edit.</div>
          ) : (
            <>
              <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
                <div>
                  <span className="label-overline">{active.is_system ? "System role" : "Custom role"}</span>
                  <h3 className="font-display text-2xl font-bold tracking-tight mt-1">{active.name}</h3>
                  <p className="text-xs text-zinc-500 mt-1 font-mono">Base: {active.base_role} · Key: {active.key}</p>
                  {active.description && <p className="text-sm text-zinc-600 mt-2">{active.description}</p>}
                </div>
                <div className="flex gap-2">
                  {active.key !== "super_admin" && (
                    <Button variant="outline" onClick={deleteRole} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-role">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  )}
                  {dirtyPerms && (
                    <>
                      <Button variant="outline" onClick={cancel} data-testid="cancel-perms">Cancel</Button>
                      <Button onClick={save} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="save-perms">
                        <Save className="w-4 h-4 mr-1" /> Save changes
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mb-4 pb-3 border-b border-zinc-100">
                <div className="text-xs text-zinc-500 font-mono">{effectivePerms.length} of {modules.length} modules enabled</div>
                <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={modules.length > 0 && effectivePerms.length === modules.length}
                    ref={(el) => {
                      if (el) el.indeterminate = effectivePerms.length > 0 && effectivePerms.length < modules.length;
                    }}
                    onChange={(e) => setDirtyPerms(e.target.checked ? modules.map((m) => m.key) : [])}
                    data-testid="edit-role-select-all"
                  />
                  Select all modules
                </label>
              </div>

              <div className="space-y-6">
                {Object.entries(grouped).map(([cat, mods]) => {
                  const catKeys = mods.map((m) => m.key);
                  const catSelected = catKeys.filter((k) => effectivePerms.includes(k)).length;
                  const allCat = catSelected === catKeys.length;
                  const someCat = catSelected > 0 && catSelected < catKeys.length;
                  return (
                    <div key={cat}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="label-overline">{cat}</div>
                        <label className="flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-zinc-900 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5"
                            checked={allCat}
                            ref={(el) => { if (el) el.indeterminate = someCat; }}
                            onChange={(e) => {
                              setDirtyPerms(
                                e.target.checked
                                  ? Array.from(new Set([...effectivePerms, ...catKeys]))
                                  : effectivePerms.filter((p) => !catKeys.includes(p))
                              );
                            }}
                            data-testid={`edit-role-cat-${cat}`}
                          />
                          Select all
                        </label>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {mods.map((m) => {
                          const enabled = effectivePerms.includes(m.key);
                          return (
                            <label
                              key={m.key}
                              className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${enabled ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:bg-zinc-50"}`}
                              data-testid={`perm-toggle-${active.key}-${m.key}`}
                            >
                              <input
                                type="checkbox"
                                checked={enabled}
                                onChange={() => togglePerm(m.key)}
                                className="w-4 h-4"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium">{m.label}</div>
                                <div className="text-[10px] text-zinc-500 font-mono">{m.key}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Departments management */}
      <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="departments-section">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-zinc-700" />
              <h2 className="font-display text-2xl font-black tracking-tight">Departments</h2>
            </div>
            <p className="text-xs text-zinc-500 mt-1 max-w-lg">
              Departments are used to group staff (Housekeeping, F&B, etc.) and filter the Staff and Team Tenure pages. System departments cannot be deleted.
            </p>
          </div>
        </div>

        {isSuperAdmin && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="department-create-row">
            <Input
              value={newDeptName}
              onChange={(e) => setNewDeptName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createDepartment(); } }}
              placeholder="New department name (e.g. Spa)"
              className="max-w-xs"
              data-testid="department-name-input"
            />
            <Button
              onClick={createDepartment}
              disabled={!newDeptName.trim() || deptBusy}
              className="bg-zinc-950 hover:bg-zinc-800"
              data-testid="department-create-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add department
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-2" data-testid="department-list">
          {departments.length === 0 && (
            <div className="text-sm text-zinc-500">No departments yet.</div>
          )}
          {departments.map((d) => (
            <div
              key={d.id}
              className="inline-flex items-center gap-2 border border-zinc-200 rounded-full pl-3 pr-1.5 py-1 text-sm"
              data-testid={`department-chip-${d.id}`}
            >
              <span className="text-zinc-900">{d.name}</span>
              {d.is_system ? (
                <span className="text-[10px] uppercase tracking-widest text-zinc-400 inline-flex items-center gap-1">
                  <Lock className="w-3 h-3" /> System
                </span>
              ) : isSuperAdmin && (
                <button
                  onClick={() => setDeptToDelete(d)}
                  className="w-6 h-6 rounded-full hover:bg-red-50 text-zinc-500 hover:text-red-600 flex items-center justify-center"
                  data-testid={`department-delete-${d.id}`}
                  aria-label={`Delete ${d.name}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Delete department confirm */}
      <AlertDialog open={!!deptToDelete} onOpenChange={(o) => !o && setDeptToDelete(null)}>
        <AlertDialogContent data-testid="dept-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete department "{deptToDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. If any staff are still in this department, the delete will be blocked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dept-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deptToDelete && deleteDepartment(deptToDelete)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="dept-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
